import { useState } from 'react'
import { AlertCircle, X, ExternalLink, Copy, Check } from 'lucide-react'
import { cn } from '../lib/utils'

/**
 * Props interface for the DevnetBanner component
 * Defines configuration options for the devnet warning banner
 */
interface DevnetBannerProps {
  /** Whether the banner can be dismissed */
  dismissible?: boolean
  /** Callback when banner is dismissed */
  onDismiss?: () => void
  /** Whether to show detailed network information */
  showDetails?: boolean
  /** Custom className for styling */
  className?: string
  /** Network being used (from environment) */
  network?: string
  /** RPC endpoint being used */
  rpcEndpoint?: string
  /** Program ID being used */
  programId?: string
}

/**
 * DevnetBanner Component
 * 
 * Displays a prominent banner indicating the app is connected to Solana Devnet.
 * Provides important context for users testing the application and includes:
 * - Clear network status indication
 * - Warning about test environment
 * - Links to get devnet SOL from faucets
 * - Configuration details when expanded
 * - WCAG 2.1 AA compliant accessibility
 * 
 * Features:
 * - Dismissible option for improved UX
 * - Expandable details section
 * - Copy-to-clipboard for addresses
 * - Responsive design
 * - Smooth animations
 */
export function DevnetBanner({
  dismissible = true,
  onDismiss,
  showDetails = false,
  className,
  network = 'devnet',
  rpcEndpoint = 'https://api.devnet.solana.com',
  programId
}: DevnetBannerProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(showDetails)
  const [copied, setCopied] = useState<string | null>(null)

  /**
   * Handle copy to clipboard with user feedback
   * Provides visual confirmation when text is copied
   */
  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  /**
   * Handle banner dismissal
   */
  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss()
    }
  }

  return (
    <div
      className={cn(
        'relative w-full bg-gradient-to-r from-status-warning/20 via-cyber-yellow/10 to-status-warning/20',
        'border-y border-status-warning/30 backdrop-blur-sm',
        'animate-slide-down',
        className
      )}
      role="banner"
      aria-label="Devnet environment notice"
    >
      <div className="container mx-auto px-4 lg:px-6 py-3">
        <div className="flex items-start gap-3">
          {/* Warning Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-2 bg-status-warning/20 rounded-cyber">
              <AlertCircle 
                className="w-5 h-5 text-status-warning" 
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-header text-status-warning text-sm lg:text-base font-semibold">
                  Connected to Solana Devnet (Test Network)
                </h3>
                <p className="text-text-secondary text-xs lg:text-sm mt-1">
                  This is a test environment. No real funds are at risk. Use devnet SOL for testing.
                </p>
              </div>

              {/* Dismiss Button */}
              {dismissible && (
                <button
                  onClick={handleDismiss}
                  className={cn(
                    'flex-shrink-0 p-1.5 rounded-cyber transition-all duration-200',
                    'hover:bg-status-warning/20 focus-cyber',
                    'text-text-muted hover:text-status-warning'
                  )}
                  aria-label="Dismiss devnet banner"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 mt-3">
              <a
                href="https://faucet.solana.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-cyber',
                  'bg-status-warning/20 hover:bg-status-warning/30',
                  'text-status-warning hover:text-status-warning',
                  'border border-status-warning/30 hover:border-status-warning/50',
                  'text-xs font-medium transition-all duration-200',
                  'focus-cyber interactive'
                )}
                aria-label="Get devnet SOL from Solana faucet"
              >
                <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                <span>Get Devnet SOL</span>
              </a>

              <button
                onClick={() => setExpanded(!expanded)}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-cyber',
                  'bg-dark-panel/50 hover:bg-dark-panel',
                  'text-text-secondary hover:text-cyber-cyan',
                  'border border-dark-border hover:border-cyber-cyan/30',
                  'text-xs font-medium transition-all duration-200',
                  'focus-cyber interactive'
                )}
                aria-label={expanded ? 'Hide network details' : 'Show network details'}
                aria-expanded={expanded}
              >
                <span>{expanded ? 'Hide' : 'Show'} Details</span>
              </button>
            </div>

            {/* Expanded Details */}
            {expanded && (
              <div
                className={cn(
                  'mt-3 p-3 bg-dark-surface/50 rounded-cyber border border-dark-border',
                  'animate-slide-down'
                )}
                role="region"
                aria-label="Network configuration details"
              >
                <div className="space-y-2">
                  {/* Network */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted font-medium">Network:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-status-warning">
                        {network.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* RPC Endpoint */}
                  <div className="flex items-center justify-between text-xs gap-3">
                    <span className="text-text-muted font-medium flex-shrink-0">RPC Endpoint:</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-text-secondary truncate flex-1 text-right">
                        {rpcEndpoint}
                      </span>
                      <button
                        onClick={() => handleCopy(rpcEndpoint, 'rpc')}
                        className={cn(
                          'flex-shrink-0 p-1 rounded hover:bg-dark-panel/50 transition-colors',
                          'text-text-muted hover:text-cyber-cyan focus-cyber'
                        )}
                        aria-label="Copy RPC endpoint to clipboard"
                      >
                        {copied === 'rpc' ? (
                          <Check className="w-3.5 h-3.5 text-status-success" aria-hidden="true" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Program ID */}
                  {programId && (
                    <div className="flex items-center justify-between text-xs gap-3">
                      <span className="text-text-muted font-medium flex-shrink-0">Program ID:</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-text-secondary truncate flex-1 text-right">
                          {programId}
                        </span>
                        <button
                          onClick={() => handleCopy(programId, 'program')}
                          className={cn(
                            'flex-shrink-0 p-1 rounded hover:bg-dark-panel/50 transition-colors',
                            'text-text-muted hover:text-cyber-cyan focus-cyber'
                          )}
                          aria-label="Copy program ID to clipboard"
                        >
                          {copied === 'program' ? (
                            <Check className="w-3.5 h-3.5 text-status-success" aria-hidden="true" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Explorer Link */}
                  {programId && (
                    <div className="pt-2 border-t border-dark-border/50">
                      <a
                        href={`https://explorer.solana.com/address/${programId}?cluster=devnet`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'inline-flex items-center gap-2 text-xs',
                          'text-cyber-cyan hover:text-cyber-cyan/80',
                          'transition-colors duration-200'
                        )}
                        aria-label="View program on Solana Explorer"
                      >
                        <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                        <span>View on Solana Explorer</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animated border effect - decorative only */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-status-warning to-transparent opacity-50"
        aria-hidden="true"
      />
    </div>
  )
}

export default DevnetBanner
